/* "Google instant" suggestions under a description box (the Timer card's "What are you working on?", and the Description
   in the add / edit entry window).
   Typed words match the START of words only, in any order ("tim" finds "Time Clock …", never "esTIMates"); the matched
   word starts are highlighted in the list, so it is clear why something is there. (See ../lib/match.ts.)
   - Type @ and a few letters: the projects whose project / client / task names have every typed word are
     listed under the box, the ones this person used most recently first. One tap (or arrow keys + Enter) sets client,
     project and task, and the "@…" text is taken out of the description (the rest stays).
   - Without @, after 2 letters: this person's earlier entries whose description (or project / client / task) matches:
     description matches first, then project / client / task matches, newest first within each; each description +
     project + task once. A tap fills the description and client / project / task. An entry with no description is
     listed under its project's name; a tap on it sets client / project / task and leaves the description empty.
     The typed search letters are never kept: a pick always takes the entry's own description (owner, 2026-10-13).
   Members and the bookkeeper only see projects assigned to them (both kinds of suggestion).
   - Timer card only: tapping the empty box lists the 5 most recently used description + project + task under "Recent"
     (newest first, same rows and the same rules as above); typing switches to the matching above, clearing shows Recent again.
   Same code, same list for typed text on the Timer card and the add / edit window (the person: the Team member box, else whoever
   is signed in).
   Typing itself is never changed unless a suggestion is chosen. Nothing is highlighted until the arrow keys move to a row,
   so a plain Enter keeps what was typed and picks nothing. Escape or a tap elsewhere closes the list. The list lives
   outside the redrawn screen, so a background redraw (which recreates the box) neither closes nor redraws it. */
import {State, byId, mayRecord} from '../state.ts';
import {escapeHtml} from './common.ts';
import {clientOptions, projectOptions, taskOptions} from './pickers.ts';
import {queryWords as words, matchesAll, markStarts} from '../lib/match.ts';

export interface Suggestion { projectId: string; taskId: string; desc?: string }   // desc: only for an earlier entry with a description
interface Item extends Suggestion { title: string; sub: string; entry: boolean }
export interface SuggestCfg { who: () => string; onPick: (s: Suggestion, desc: string) => void; recent?: boolean }   // recent: the Timer card's "Recent" on an empty box

const MAX = 40, ROW = 54, SHOW = 8, RECENT = 5;
const swapped = new WeakSet<HTMLInputElement>();       // a box just recreated by a redraw (its focus is the redraw's, not a tap)
let box: HTMLDivElement | null = null;
let cur: {input: HTMLInputElement; cfg: SuggestCfg; items: Item[]; q: string[]; active: number; at: number; caret: number; scrolled: boolean} | null = null;

const newest = (a: {startedAt: string}, b: {startedAt: string}) => (b.startedAt || '').localeCompare(a.startedAt || '');
/** the "@query" being typed: from an @ (at the start or after a space) up to the cursor */
function atQuery(v: string, caret: number): {at: number; q: string} | null {
  const before = v.slice(0, caret), i = before.lastIndexOf('@');
  if (i < 0 || (i > 0 && !/\s/.test(before[i - 1]))) return null;
  return {at: i, q: before.slice(i + 1)};
}
/** every project (and project + task) this person may pick, matching all the words; recently used first */
function projectItems(member: string, q: string[]): Item[] {
  const rank = new Map<string, number>();
  State.entries.filter(e => e.memberId === member).sort(newest).forEach(e => { const k = e.projectId + '|' + (e.taskId || ''); if (!rank.has(k)) rank.set(k, rank.size); });
  const out: (Item & {r: number})[] = [];
  for (const c of clientOptions(member)) for (const p of projectOptions(c.id, member)) for (const t of taskOptions(p.id, member)) {
    if (matchesAll([p.label, c.label, c.keywords || '', t.id ? t.label : ''].join(' \n '), q))
      out.push({projectId: p.id, taskId: t.id, title: p.label, sub: [c.label, t.id ? t.label : ''].filter(Boolean).join(' · '), entry: false, r: rank.get(p.id + '|' + t.id) ?? Infinity});
  }
  return out.sort((a, b) => a.r - b.r).slice(0, MAX);
}
/** this person's earlier entries matching what is typed (description + project + task once each): description matches
    first, then project / client / task matches; newest first within each */
function entryItems(member: string, text: string, recent = false): Item[] {
  const q = words(text), typed = text.trim().toLowerCase();
  if (typed.length < 2 && !recent) return [];
  const seen = new Set<string>(), first: Item[] = [], then: Item[] = [];
  for (const e of State.entries.filter(x => x.memberId === member).sort(newest)) {
    const p = byId(State.projects, e.projectId), c = p && byId(State.clients, p.clientId);
    if (!p || !p.active || !c || !c.active || !mayRecord(p.id)) continue;   // only projects that can still be picked (and are assigned)
    const t = e.taskId ? byId(State.tasks, e.taskId) : undefined, taskId = t && t.active ? t.id : '';
    const desc = (e.description || '').trim(), k = desc.toLowerCase() + '|' + p.id + '|' + taskId;
    if (seen.has(k)) continue; seen.add(k);
    const sub = [p.name, c.name, taskId ? t!.name : ''].filter(Boolean).join(' · ');
    if (recent) {   // the latest ones, whatever they are (an empty box: nothing typed to match)
      first.push(desc ? {projectId: p.id, taskId, desc, title: desc, sub, entry: true} : {projectId: p.id, taskId, title: p.name, sub: [c.name, taskId ? t!.name : ''].filter(Boolean).join(' · '), entry: true});
      if (first.length >= RECENT) break;
      continue;
    }
    if (desc && matchesAll(desc, q)) first.push({projectId: p.id, taskId, desc, title: desc, sub, entry: true});
    else if (matchesAll([desc, p.name, c.name, taskId ? t!.name : ''].join(' \n '), q))
      then.push(desc ? {projectId: p.id, taskId, desc, title: desc, sub, entry: true} : {projectId: p.id, taskId, title: p.name, sub: [c.name, taskId ? t!.name : ''].filter(Boolean).join(' · '), entry: true});   // no description: the project is the title
    if (first.length >= MAX) break;
  }
  return [...first, ...then].slice(0, MAX);
}

function ensureBox(): HTMLDivElement {
  if (box) return box;
  box = document.createElement('div');
  box.className = 'sug'; box.id = 'sugList'; box.setAttribute('role', 'listbox'); box.hidden = true;
  box.addEventListener('mousedown', e => e.preventDefault());               // a click keeps the cursor in the box (computer)
  box.addEventListener('click', e => { const o = (e.target as HTMLElement).closest<HTMLElement>('[data-i]'); if (o) pick(+o.dataset.i!); });
  document.body.append(box);
  document.addEventListener('pointerdown', e => {                         // a tap elsewhere closes it
    if (cur && box && !box.hidden && !box.contains(e.target as Node) && e.target !== cur.input) close();
  }, true);
  addEventListener('scroll', position, true); addEventListener('resize', position);
  window.visualViewport?.addEventListener('resize', position); window.visualViewport?.addEventListener('scroll', position);
  return box;
}
export function closeSuggest() { close(); }
function close() { if (box) { box.hidden = true; box.innerHTML = ''; } if (cur) cur.items = []; }

function update() {
  if (!cur) return;
  const v = cur.input.value, caret = cur.input.selectionStart ?? v.length, aq = atQuery(v, caret);
  cur.at = aq ? aq.at : -1; cur.caret = caret; cur.active = -1;
  cur.q = words(aq ? aq.q : v);
  const recent = !aq && !v.trim() && !!cur.cfg.recent;
  cur.items = aq ? projectItems(cur.cfg.who(), cur.q) : entryItems(cur.cfg.who(), v, recent);
  paint(!!aq, recent ? 'Recent' : 'Earlier entries');
}
function paint(at: boolean, head: string) {
  const b = ensureBox();
  if (!cur || (!cur.items.length && !at)) { close(); return; }
  b.innerHTML = cur.items.length ? (at ? '' : `<div class="sug-head">${head}</div>`) + cur.items.map((o, i) =>
    `<div class="sug-opt ${i === cur!.active ? 'active' : ''} ${o.entry ? 'ent' : 'proj'}" role="option" data-i="${i}"><b>${markStarts(o.title, cur!.q, escapeHtml)}</b><span>${markStarts(o.sub, cur!.q, escapeHtml)}</span></div>`).join('')
    : '<div class="sug-none">No project matches — keep typing, or remove the @</div>';
  const wasHidden = b.hidden; b.hidden = false;
  if (wasHidden) cur.scrolled = false;
  position();
}
/** under the box, as wide as it; no taller than the space above the iPhone keyboard (about 8 rows at most) */
function position() {
  if (!cur || !box || box.hidden) return;
  if (!cur.input.isConnected) {   // redrawn: follow the new copy of the box (or close when it is gone)
    const n = document.getElementById(cur.input.id);
    if (n instanceof HTMLInputElement) cur.input = n; else { close(); return; }
  }
  const vv = window.visualViewport, top = vv ? vv.offsetTop : 0, bottom = vv ? vv.offsetTop + vv.height : innerHeight;
  let r = cur.input.getBoundingClientRect();
  if (!cur.scrolled && bottom - r.bottom < 4 * ROW && !cur.input.closest('.modal')) {   // little room above the keyboard: move the box up
    cur.scrolled = true; window.scrollBy(0, r.top - top - 70); r = cur.input.getBoundingClientRect();
  }
  const w = Math.min(Math.max(r.width, 280), innerWidth - 16);
  box.style.left = Math.max(8, Math.min(innerWidth - w - 8, r.left)) + 'px';
  box.style.top = (r.bottom + 4) + 'px'; box.style.width = w + 'px';
  box.style.maxHeight = Math.max(ROW * 2, Math.min(ROW * SHOW, bottom - r.bottom - 12)) + 'px';
}
function pick(i: number) {
  if (!cur) return;
  const it = cur.items[i]; if (!it) return;
  const {input, cfg} = cur, v = input.value;
  let desc: string;
  if (it.entry) desc = it.desc ?? '';                                       // an earlier entry: exactly its description (none: empty), never the typed search
  else { const l = v.slice(0, cur.at).trim(), r = v.slice(cur.caret).trim(); desc = l && r ? l + ' ' + r : l + r; }   // @project: the "@…" is taken out, the rest stays
  input.value = desc;
  close();
  cfg.onPick({projectId: it.projectId, taskId: it.taskId, desc: it.desc}, desc);
  input.blur();                                                           // the keyboard goes away: the card shows the choice
  const a = document.activeElement;                                       // (a redraw may have put the cursor in the new copy of the box)
  if (a instanceof HTMLElement && a.id && a.id === input.id) a.blur();
  close();
}
function onKey(ev: KeyboardEvent) {
  if (!cur || !box || box.hidden) return;
  const n = cur.items.length;
  if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
    if (!n) return;
    ev.preventDefault();
    cur.active = ev.key === 'ArrowDown' ? Math.min(n - 1, cur.active + 1) : Math.max(-1, cur.active - 1);
    box.querySelectorAll('.sug-opt').forEach((o, i) => o.classList.toggle('active', i === cur!.active));
    box.querySelector('.sug-opt.active')?.scrollIntoView({block: 'nearest'});
  } else if (ev.key === 'Enter' && cur.active >= 0) { ev.preventDefault(); ev.stopImmediatePropagation(); pick(cur.active); }
  else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); }
}

/** suggestions for this description box (call on every redraw: an open list carries on under the new box) */
export function wireSuggest(input: HTMLInputElement | null, cfg: SuggestCfg) {
  if (!input) return;
  ensureBox();
  const open = () => { if (!cur || cur.input !== input) cur = {input, cfg, items: [], q: [], active: -1, at: -1, caret: 0, scrolled: false}; update(); };
  input.addEventListener('input', open);
  if (cfg.recent) {   // the Timer card: a tap on the empty box shows Recent (not when a redraw puts the cursor back)
    const recent = () => { if (!swapped.has(input) && !input.value.trim() && (!box || box.hidden)) open(); };
    input.addEventListener('focus', recent); input.addEventListener('click', recent);
  }
  input.addEventListener('keydown', onKey);
  input.addEventListener('focusout', () => setTimeout(() => {   // left the box (a redraw puts the cursor straight back: then it stays)
    const a = document.activeElement;
    if (cur && box && !box.hidden && !(a && a.id === cur.input.id) && !box.contains(a)) close();
  }, 250));
  if (cur && cur.input !== input && cur.input.id === input.id) { cur.input = input; cur.cfg = cfg; requestAnimationFrame(position); }
  swapped.add(input); setTimeout(() => swapped.delete(input), 0);   // the redraw's own focus comes straight after this
}
